import * as React from 'react'
// libs
import styled from 'styled-components'
import { useSelector } from 'react-redux'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import { components } from 'react-select'
import { HoverablePopover } from 'components/UI'
import { OpenRiskLink } from 'components/FormElements/components/OpenRiskLink'
// constants
import { Modals } from 'constants/modals'
import { ENTITIES } from 'constants/index'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// types
import { IOption } from 'components/FormElements/Select'
import { MultiValueLabelProps } from 'components/FormElements/components/MultiValueLabel'
// utils
import { find, get, pipe } from 'lodash/fp'
import { startCase, toLower } from 'lodash'
import { generateFieldSource } from 'utils/form'

const Wrapper = styled.div`
  pointer-events: all;
  white-space: nowrap;
`

const BaseMultiValueLabel: React.FC<MultiValueLabelProps> = (props) => {
  const { data, selectProps } = props

  const riskTemplates = useSelector(riskTemplatesSelector)

  const { hideModal: hideActionModal, showModal: showActionModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })
  const { hideModal: hideKPIModal, showModal: showKPIModal } = useShowModal({ modalType: Modals.KPI_MINIMIZED_MODAL })
  const { hideModal: hideObjectiveModal, showModal: showObjectiveModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })

  const onMouseDown = (e: React.MouseEvent) => e.stopPropagation()

  const isRisk = data.entity === ENTITIES.RISK && !!data.riskTemplateId

  const label = isRisk ? (
    <components.MultiValueLabel {...props}>
      <OpenRiskLink riskId={data.value} riskTemplateId={data.riskTemplateId}>
        {props.children}
      </OpenRiskLink>
    </components.MultiValueLabel>
  ) : (
    <components.MultiValueLabel {...props} />
  )

  const valueGroup = pipe(
    get('options'),
    find(({ options }) => options?.some(({ value }) => value === data.value)),
    get('label')
  )(selectProps)

  const valueSource = pipe(
    get('value'),
    () => {
      if (Array.isArray(selectProps.value)) {
        return selectProps.value.find(({ value }) => value === data.value)
      }
      return (selectProps.value as IOption).value === data.value
    },
    ({ entity }) => startCase(toLower(generateFieldSource(entity)))
  )(selectProps)

  const templateName = riskTemplates?.find(item => item.id === data.riskTemplateId)?.name

  if (!valueGroup && !valueSource) {
    return <Wrapper onMouseDown={onMouseDown}>{label}</Wrapper>
  }

  const handleObjectiveClick = () => {
    showObjectiveModal({ id: props.data.value })
    hideActionModal()
    hideKPIModal()
  }

  const handleActionClick = () => {
    showActionModal({ id: props.data.value })
    hideObjectiveModal()
    hideKPIModal()
  }

  const handleKPIClick = () => {
    showKPIModal({ id: props.data.value })
    hideObjectiveModal()
    hideActionModal()
  }

  const onMultiValueClick = () => {
    if (props.data.entity === ENTITIES.PLAN_ACTION) {
      handleActionClick()
    }
    if (props.data.entity === ENTITIES.PLAN_OBJECTIVE) {
      handleObjectiveClick()
    }
    if (props.data.entity === ENTITIES.PLAN_KPI) {
      handleKPIClick()
    }
  }

  return (
    <HoverablePopover
      trigger={
        <Wrapper onMouseDown={onMouseDown} onClick={onMultiValueClick}>
          {label}
        </Wrapper>
      }>
      <b>{templateName || valueGroup || valueSource}: </b> {data.label}
    </HoverablePopover>
  )
}

export default BaseMultiValueLabel
